/**
 * An article-owned document attachment (#1637, comment 43534).
 *
 * The second of the renderer's TWO finite constructs. It is not a marker in
 * the body: a document belongs to the ARTICLE, so it is drawn once, below the
 * prose, as an explicit card carrying title, format, size, revision and source
 * provenance — the four things a reader needs to know whether the PDF in front
 * of them is the one they were told about.
 *
 * ── WHY A BUTTON AND NOT A LINK ────────────────────────────────────────────
 * An `<a href>` needs a URL at render time, and there is none: obtaining one
 * is a governed act that re-asks visibility and records issuance. Rendering an
 * anchor would mean minting a bounded URL for every document on the page
 * whether or not anyone opens it, which turns a receipt into noise and hands
 * out reach nobody asked for. So the control is a button, the URL is minted on
 * activation, and the receipt means what it says.
 *
 * ── THE STATES ─────────────────────────────────────────────────────────────
 *   idle     the card, ready
 *   opening  `aria-busy`, the control disabled, so a second press cannot mint
 *            a second receipt for the same intent
 *   failed   an accessible message and Try again, with the card intact
 * A refusal from the server is shown as the server's own sentence only where
 * it is safe to; otherwise it is a plain apology and a retry, because a
 * customer must never be shown a schema name or a SQL fragment.
 */
import { useRef, useState } from 'react'
import { Button } from '../controls/Button'
import { formatBytes, formatLabel } from './articleTokens'
import type { KbAsset } from './types'
import s from './kb.module.css'

export interface KbDocumentCardProps {
  articleId: string
  asset: KbAsset
  requestUrl: (input: { articleId: string; assetKey: string }) => Promise<string>
  /** Injected so a test can assert what would have been opened. */
  open?: (url: string) => void
}

export function KbDocumentCard({ articleId, asset, requestUrl, open }: KbDocumentCardProps) {
  const [busy, setBusy] = useState(false)
  const [failed, setFailed] = useState<string | null>(null)
  const btn = useRef<HTMLButtonElement | null>(null)

  const title = asset.caption ?? asset.alt_text ?? asset.asset_key
  const format = formatLabel(asset.content_type)
  const size = formatBytes(asset.size_bytes)
  const meta = [format, size, asset.revision ? `Revision ${asset.revision}` : null]
    .filter(Boolean).join(' · ')
  const provenance = [
    asset.source_ref,
    asset.source_commit ? `at ${asset.source_commit}` : null,
  ].filter(Boolean).join(' ')
  const metaId = `kb-doc-meta-${asset.asset_key}`

  const activate = async () => {
    setBusy(true)
    setFailed(null)
    try {
      const url = await requestUrl({ articleId, assetKey: asset.asset_key })
      if (open) open(url)
      // `noopener` is not optional on a window opened to a bounded URL: without
      // it the opened document holds a live `window.opener` handle on the
      // portal session's origin.
      else globalThis.window?.open(url, '_blank', 'noopener,noreferrer')
      // Since #1637 phase B the URL is a `blob:` handle on bytes this document
      // already holds, so it is a RESOURCE and not a link. It cannot be revoked
      // straight away — the new tab has to finish loading from it first — and it
      // must not be left for ever, or every document a reader opens stays
      // decoded in this tab's memory. Sixty seconds is the window the signed URL
      // it replaced used to have, kept so the behaviour a reader sees is the
      // same one. Revoking is a no-op on anything that is not a blob handle.
      globalThis.setTimeout?.(() => { URL.revokeObjectURL(url) }, 60_000)
    } catch (e) {
      const msg = e instanceof Error ? e.message : ''
      setFailed(/fetch|network|offline/i.test(msg)
        ? 'The file store could not be reached.'
        : 'That document couldn’t be opened just now.')
      btn.current?.focus()
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className={s.docCard} data-testid={`kb-doc-${asset.asset_key}`}>
      <div className={s.docBody}>
        <p className={s.docTitle}>{title}</p>
        <p className={s.docMeta} id={metaId}>{meta}</p>
        {provenance && (
          <p className={s.docProvenance}>
            {/* Provenance, said plainly. A customer reading a generated manual
                is entitled to know which source it came from. */}
            Generated from {provenance}
          </p>
        )}
        {failed && (
          <p className={s.stateText} role="status" data-testid={`kb-doc-failed-${asset.asset_key}`}>
            {failed}
          </p>
        )}
      </div>
      <div className={s.docAction}>
        <Button
          ref={btn}
          onClick={() => void activate()}
          disabled={busy}
          aria-busy={busy || undefined}
          aria-describedby={metaId}
          data-testid={`kb-doc-open-${asset.asset_key}`}
        >
          {busy ? 'Opening…' : failed ? 'Try again' : `Open ${format}`}
        </Button>
      </div>
    </div>
  )
}
